import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import api, { gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, FileText, RefreshCw, Check, AlertTriangle, Send, Loader2, X, ChevronsUpDown, Link2, Plus,
} from "lucide-react";
import DatePicker, { parseDate, fmtDate } from "@/components/DatePicker";
import SendReviewModal from "@/components/SendReviewModal";
import ProposalVideoManager from "@/components/ProposalVideoManager";
import ProposalAttachment from "@/components/ProposalAttachment";

// Searchable single-select: type the start of a name to filter instead of scrolling a long list.
function SearchSelect({ value, onChange, options, placeholder = "Select", searchPlaceholder = "Type to search…", testid, disabled }) {
  const [open, setOpen] = useState(false);
  const sel = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled}
          className="w-full justify-between font-normal" data-testid={testid}>
          <span className={sel ? "truncate" : "text-cass-slate truncate"}>{sel ? sel.label : placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={testid ? `${testid}-input` : undefined} />
          <CommandList>
            <CommandEmpty>No matches.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o.value} value={o.label} onSelect={() => { onChange(o.value); setOpen(false); }}
                  data-testid={testid ? `${testid}-opt-${o.value}` : undefined}>
                  <Check className={"w-4 h-4 mr-2 " + (o.value === value ? "opacity-100" : "opacity-0")} />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const STEPS = ["Type", "Client", "Select Services", "Configure Pricing", "Review", "Content & Send"];
const STEP_THEME = [
  { strip: "bg-slate-500", tile: "bg-slate-50", border: "border-slate-200", dot: "bg-slate-500", ring: "ring-slate-200", text: "text-slate-700", textStrong: "text-slate-800", line: "bg-slate-400" },
  { strip: "bg-teal-700", tile: "bg-teal-50", border: "border-teal-200", dot: "bg-teal-600", ring: "ring-teal-200", text: "text-teal-700", textStrong: "text-teal-800", line: "bg-cass-teal" },
  { strip: "bg-sky-700", tile: "bg-sky-50", border: "border-sky-200", dot: "bg-sky-600", ring: "ring-sky-200", text: "text-sky-700", textStrong: "text-sky-800", line: "bg-sky-500" },
  { strip: "bg-violet-700", tile: "bg-violet-50", border: "border-violet-200", dot: "bg-violet-600", ring: "ring-violet-200", text: "text-violet-700", textStrong: "text-violet-800", line: "bg-violet-500" },
  { strip: "bg-amber-600", tile: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-500", ring: "ring-amber-200", text: "text-amber-700", textStrong: "text-amber-800", line: "bg-amber-500" },
  { strip: "bg-fuchsia-700", tile: "bg-fuchsia-50", border: "border-fuchsia-200", dot: "bg-fuchsia-600", ring: "ring-fuchsia-200", text: "text-fuchsia-700", textStrong: "text-fuchsia-800", line: "bg-fuchsia-500" },
];
const STEP_SUB = ["Choose the proposal type", "Client & onboarding details", "Pick the services to include", "Configure pricing questions", "Review the numbers", "Add content & send"];
const CAT_PALETTE = [
  { borderL: "border-l-teal-500", card: "bg-teal-50/40", head: "bg-teal-100/70", text: "text-teal-800" },
  { borderL: "border-l-sky-500", card: "bg-sky-50/40", head: "bg-sky-100/70", text: "text-sky-800" },
  { borderL: "border-l-violet-500", card: "bg-violet-50/40", head: "bg-violet-100/70", text: "text-violet-800" },
  { borderL: "border-l-amber-500", card: "bg-amber-50/40", head: "bg-amber-100/70", text: "text-amber-800" },
  { borderL: "border-l-fuchsia-500", card: "bg-fuchsia-50/40", head: "bg-fuchsia-100/70", text: "text-fuchsia-800" },
  { borderL: "border-l-rose-500", card: "bg-rose-50/40", head: "bg-rose-100/70", text: "text-rose-800" },
  { borderL: "border-l-indigo-500", card: "bg-indigo-50/40", head: "bg-indigo-100/70", text: "text-indigo-800" },
  { borderL: "border-l-emerald-500", card: "bg-emerald-50/40", head: "bg-emerald-100/70", text: "text-emerald-800" },
  { borderL: "border-l-cyan-500", card: "bg-cyan-50/40", head: "bg-cyan-100/70", text: "text-cyan-800" },
  { borderL: "border-l-orange-500", card: "bg-orange-50/40", head: "bg-orange-100/70", text: "text-orange-800" },
  { borderL: "border-l-lime-600", card: "bg-lime-50/50", head: "bg-lime-100/70", text: "text-lime-800" },
  { borderL: "border-l-purple-500", card: "bg-purple-50/40", head: "bg-purple-100/70", text: "text-purple-800" },
];
const _catIndex = new Map();
const catTheme = (name) => { const k = String(name || ""); if (!_catIndex.has(k)) _catIndex.set(k, _catIndex.size); return CAT_PALETTE[_catIndex.get(k) % CAT_PALETTE.length]; };

// Live show-when visibility for a V2 pricing component, mirroring the backend engine `_cond_met`.
// Supports nested condition groups (e.g. (planA OR planB) AND payrollSelected).
const evalCond = (c, answers) => {
  if (!c || !c.conditions || !c.conditions.length) return true;
  const truthy = (v) => v === true || ["1", "true", "yes", "y", "on", "selected"].includes(String(v).toLowerCase());
  const one = (cn) => {
    if (cn && cn.conditions) return evalCond(cn, answers); // nested group
    const v = answers[cn.factor];
    switch (cn.op) {
      case "yes": case "true": return truthy(v);
      case "no": case "false": return !truthy(v);
      case "eq": return String(v).toLowerCase() === String(cn.value).toLowerCase();
      case "ne": return String(v).toLowerCase() !== String(cn.value).toLowerCase();
      case "gt": return Number(v) > Number(cn.value);
      case "lt": return Number(v) < Number(cn.value);
      default: return true;
    }
  };
  const res = c.conditions.map(one);
  return c.match === "any" ? res.some(Boolean) : res.every(Boolean);
};
const componentVisible = (comp, answers) => evalCond(comp.condition, answers);

export default function ProposalBuilder() {
  const { user } = useAuth();
  const canAmendFee = !!user && (user.role === "super_admin" || user.role === "approver" || user.can_amend_fee);
  const canSeePricingDetail = !!user && ["super_admin", "pricing_admin", "approver"].includes(user.role);
  const [params] = useSearchParams();
  const { id: editId } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(editId ? 2 : 0);
  const [maxStep, setMaxStep] = useState(editId ? 2 : 0);
  useEffect(() => { setMaxStep((m) => Math.max(m, step)); }, [step]);
  const goToStep = (i) => { if (i <= maxStep && i !== step) { setStep(i); window.scrollTo({ top: 0, behavior: "smooth" }); } };
  const [ptype, setPtype] = useState(params.get("type") === "renewal" ? "renewal" : "new");
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [clientId, setClientId] = useState(params.get("client") || "");
  const [renewalSourceId, setRenewalSourceId] = useState(params.get("renewalOf") || "");
  const [sourceProposals, setSourceProposals] = useState([]);
  const [selected, setSelected] = useState({}); // {service_id: {included, presentation}}
  const [answers, setAnswers] = useState({});
  const [manual, setManual] = useState({}); // {service_id: {fee, reason}}
  const [calc, setCalc] = useState(null);
  const [prevTotals, setPrevTotals] = useState(null);
  const [proposalId, setProposalId] = useState(editId || null);
  const [secOrder, setSecOrder] = useState([]);
  useEffect(() => { api.get("/service-sections").then((r) => setSecOrder(r.data.order || [])).catch(() => {}); }, []);
  const [pqIndex, setPqIndex] = useState(0); // sub-step within the Pricing Questions step (per catalogue section)
  const [activeSvc, setActiveSvc] = useState(null); // open service card in Configure Pricing
  const [proposalRef, setProposalRef] = useState("");
  const [structure, setStructure] = useState(() => (new URLSearchParams(window.location.search).get("structure") === "multi" ? "multi_option" : "standard"));
  const [options, setOptions] = useState([]);
  const [activeOptionId, setActiveOptionId] = useState(null);
  const [token, setToken] = useState(null);
  const [showCurrent, setShowCurrent] = useState(false);
  const [renewalExplanation, setRenewalExplanation] = useState("");
  const [catchUp, setCatchUp] = useState([]);
  const [catchUpDue, setCatchUpDue] = useState("");
  const [catchUpMethod, setCatchUpMethod] = useState("one_off");
  const [catchUpMonths, setCatchUpMonths] = useState("");
  const [catchUpPlan, setCatchUpPlan] = useState(null);
  const [yearEnd, setYearEnd] = useState("");
  const [csDate, setCsDate] = useState("");
  const [video, setVideo] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [alignOverrides, setAlignOverrides] = useState({});
  const [alignMethod, setAlignMethod] = useState("one_off");
  const [alignMonths, setAlignMonths] = useState("");
  const [alignPlan, setAlignPlan] = useState(null);
  const [oneOffItems, setOneOffItems] = useState([]);
  const [send, setSend] = useState({ recipient_email: "", recipient_name: "", message: "", access_code: "" });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentLink, setSentLink] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [linkOnly, setLinkOnly] = useState(false);
  const [signerSel, setSignerSel] = useState([]);
  const [signPolicy, setSignPolicy] = useState("all");
  const [emailCopy, setEmailCopy] = useState({ subject: "", body: "" });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  useEffect(() => { api.get("/proposal-email-send-options").then((r) => setEmailTemplates(r.data || [])).catch(() => {}); }, []);
  const [saveDefault, setSaveDefault] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [disc, setDisc] = useState({ label: "", type: "percent", value: 0, reason: "" });
  const [annualUplift, setAnnualUplift] = useState({ enabled: false, pct: "" });
  const [variationSourceId, setVariationSourceId] = useState("");
  const [variationSourceRef, setVariationSourceRef] = useState("");
  const [varDelta, setVarDelta] = useState(null);

  const defaultDeadline = () => { const d = new Date(); d.setDate(d.getDate() + 28); return fmtDate(d); };
  useEffect(() => { if (!editId && !expiryDate) setExpiryDate(defaultDeadline()); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/services").then((r) => setServices(r.data));
    api.get("/settings/annual-uplift").then((r) => setAnnualUplift((a) => (a.pct === "" ? { ...a, pct: r.data.pct } : a))).catch(() => {});
  }, []);

  // load existing proposal for edit
  const applyProposal = (p) => {
    setPtype(p.proposal_type);
    setClientId(p.client_id || "");
    setAnswers(p.answers || {});
    setProposalRef(p.reference);
    const sel = {};
    const man = {};
    (p.services || []).forEach((s) => {
      sel[s.service_id] = { included: s.included, presentation: s.presentation };
      if (s.proposed_fee !== s.recommended_fee) man[s.service_id] = { fee: s.proposed_fee, reason: s.manual_reason || "" };
    });
    setSelected(sel); setManual(man);
    setShowCurrent(!!p.show_current_fee_to_client);
    setRenewalExplanation(p.renewal_explanation || "");
    setCatchUp(p.catch_up_items || []);
    setCatchUpDue(p.catch_up_due_date || "");
    { const cup = p.catch_up_payment || {}; setCatchUpMethod(cup.method || "one_off"); setCatchUpMonths(cup.months ? String(cup.months) : ""); }
    setEffectiveDate(p.effective_date || "");
    setExpiryDate(p.expiry_date || defaultDeadline());
    setAlignOverrides(p.fee_alignment_overrides || {});
    { const fap = p.fee_alignment_payment || {}; setAlignMethod(fap.method || "one_off"); setAlignMonths(fap.months ? String(fap.months) : ""); }
    setOneOffItems(p.one_off_items || []);
    if (p.year_end) setYearEnd(p.year_end);
    if (p.cs_date) setCsDate(p.cs_date);
    setVideo(p.video || null);
    setRenewalSourceId(p.renewal_source_id || "");
    setPrevTotals(p.previous_totals || null);
    setAttachment(p.attachment || null);
    if (p.discount) setDisc({ label: p.discount.label || "", type: p.discount.type || "percent", value: p.discount.value ?? 0, reason: p.discount.reason || "" });
    if (p.annual_uplift) setAnnualUplift({ enabled: !!p.annual_uplift.enabled, pct: p.annual_uplift.pct ?? "" });
    setVariationSourceId(p.variation_source_id || "");
    setVariationSourceRef(p.variation_source_ref || "");
    if (typeof p.builder_step === "number") setStep(p.builder_step);
    if (typeof p.builder_pq_index === "number") setPqIndex(p.builder_pq_index);
  };

  useEffect(() => {
    if (!editId) return;
    api.get(`/proposals/${editId}`).then((r) => {
      const p = r.data;
      setStructure(p.structure || "standard");
      if (p.structure === "multi_option" && (p.options || []).length) {
        setOptions(p.options);
        const act = p.options[0];
        setActiveOptionId(act.id);
        applyProposal({ ...p, ...act });
      } else {
        applyProposal(p);
      }
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [editId]);

  // Prefill the send recipient from the client's signatory contact (only when empty, preserving edits).
  useEffect(() => {
    const cl = clients.find((c) => c.id === clientId);
    const contacts = cl?.contacts || [];
    const primary = contacts.find((x) => x.signing_authority) || contacts[0];
    if (!primary) return;
    const titleCase = (s) => (s || "").toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase());
    setSend((s) => ({
      ...s,
      recipient_email: s.recipient_email || primary.email || "",
      recipient_name: s.recipient_name || titleCase(primary.name) || "",
    }));
    const sigContacts = contacts.filter((x) => x.signing_authority && x.email);
    if (sigContacts.length >= 2) setSignerSel((cur) => (cur.length ? cur : sigContacts.map((c) => c.email)));
  }, [clientId, clients]);


  // load source proposals when client chosen for renewal
  useEffect(() => {
    if (ptype === "renewal" && clientId) {
      api.get(`/clients/${clientId}`).then((r) =>
        setSourceProposals((r.data.proposals || []).filter((p) => p.status === "accepted")));
    }
  }, [ptype, clientId]);

  useEffect(() => {
    if (!clientId) return;
    api.get(`/clients/${clientId}`).then((r) => {
      const c = r.data;
      if (c.year_end) setYearEnd((v) => v || c.year_end);
      if (c.cs01_date) setCsDate((v) => v || c.cs01_date);
    }).catch(() => {});
  }, [clientId]);

  useEffect(() => {
    if (ptype === "variation" && editId) api.get(`/proposals/${editId}/variation-delta`).then((r) => setVarDelta(r.data)).catch(() => {});
  }, [ptype, editId, step]);

  const prefillRenewal = async (srcId, recurringOnly = false) => {
    setRenewalSourceId(srcId);
    const { data } = await api.get(`/proposals/${srcId}/renewal-prefill`);
    setAnswers(data.answers || {});
    const sel = {};
    (data.services || []).forEach((s) => {
      if (recurringOnly && s.presentation === "one_off") return; // renewal-from-tile: recurring items only
      sel[s.service_id] = { included: s.included, presentation: s.presentation };
    });
    setSelected(sel);
    if (recurringOnly) setOneOffItems([]); // drop ad-hoc one-off charges too
    setPrevTotals(data.previous_totals);
    if (data.annual_uplift?.enabled) {
      setAnnualUplift({ enabled: true, pct: data.annual_uplift.pct });
      const uf = data.uplifted_fees || {};
      if (Object.keys(uf).length) {
        setManual((m) => { const nm = { ...m }; Object.entries(uf).forEach(([sid, fee]) => { nm[sid] = { fee, reason: `Annual uplift +${data.annual_uplift.pct}%` }; }); return nm; });
      }
    }
    toast.success(recurringOnly ? "Imported recurring services (one-offs excluded)" : "Imported existing services and assumptions");
  };

  // Auto pre-fill when arriving from a "Create renewal" link (?renewalOf=…), recurring-only when flagged.
  useEffect(() => {
    const rof = params.get("renewalOf");
    if (rof && !editId) prefillRenewal(rof, params.get("recurringOnly") === "1");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedServices = useMemo(
    () => services.filter((s) => selected[s.id]?.included),
    [services, selected]
  );

  // Visibility context: answers plus virtual "service:<internal_name>" flags for each selected
  // service, so a component can be gated on another service being in the proposal (mirrors backend).
  const evalAnswers = useMemo(() => {
    const flags = {};
    selectedServices.forEach((s) => {
      if (s.internal_name) flags[`service:${s.internal_name}`] = true;
      if (String(s.category || "").toLowerCase().includes("payroll")) flags["service_payroll_selected"] = true;
    });
    return { ...answers, ...flags };
  }, [answers, selectedServices]);

  // Auto-fill the Xero "Total number of payroll employees" from the Payroll service headcount
  // (Employees + Variable pay) when both services are selected. The engine still subtracts the
  // plan's included allowance when charging, so this shows the true total headcount.
  const payrollDrivesEmployees = useMemo(
    () => selectedServices.some((s) => String(s.category || "").toLowerCase().includes("payroll"))
      && selectedServices.some((s) => s.internal_name === "xero" || (s.pricing_v2?.components || []).some((c) => c.key === "software_xero")),
    [selectedServices]
  );
  useEffect(() => {
    if (!payrollDrivesEmployees) return;
    const total = Number(answers.pr_employees || 0) + Number(answers.pr_variable_employees || 0);
    if (Number(answers.total_employees || 0) !== total) {
      setAnswers((a) => ({ ...a, total_employees: total }));
    }
  }, [payrollDrivesEmployees, answers.pr_employees, answers.pr_variable_employees]);

  // Configure Pricing: one card per selected service. Questions are grouped by their admin "group"
  // and ordered by the service's pricing-rule steps. Answers are keyed by factor, so a shared question
  // answered under one service auto-fills the others.
  const pqServices = useMemo(() => {
    const catRank = (c) => { const i = secOrder.indexOf(c); return i < 0 ? 999 : i; };
    const ordered = [...selectedServices].sort(
      (a, b) => (catRank(a.category) - catRank(b.category)) || ((a.display_order ?? 999) - (b.display_order ?? 999)));
    // Pricing Engine V2: build questions from the service's pricing components.
    const buildV2 = (svc) => (svc.pricing_v2.components || [])
      .filter((c) => c.active !== false && c.status !== "draft" && !["service_selection", "automatic"].includes(c.input_type) && componentVisible(c, evalAnswers))
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
      .map((c) => {
        if (c.input_type === "service_ref") {
          const opts = [["all", "All recurring services"],
            ...selectedServices.filter((s) => (s.pricing?.billing_frequency) === "monthly")
              .map((s) => [s.internal_name, s.client_name])];
          return {
            key: c.key, label: c.label || c.name, help_text: c.help_text || "",
            required: !!c.required, group: (c.group && c.group.trim()) || "General",
            field_type: "select", options: opts, _default: "all",
          };
        }
        const ft = c.input_type === "yes_no" ? "boolean" : c.input_type === "dropdown" ? "select" : c.input_type === "text" ? "text" : "number";
        const isNone = (o) => String(o.name || "").trim().toLowerCase() === "none" || String(o.key || "").trim().toLowerCase() === "none";
        const options = (c.options || [])
          .filter((o) => o.active !== false)
          .map((o, idx) => ({ o, idx }))
          .sort((a, b) => {
            const an = isNone(a.o) ? 0 : 1;
            const bn = isNone(b.o) ? 0 : 1;
            if (an !== bn) return an - bn;
            return ((a.o.order ?? a.idx) - (b.o.order ?? b.idx));
          })
          .map(({ o }) => [o.key || o.name, o.name]);
        let _default;
        if (ft === "boolean") {
          _default = (c.default === true || ["yes", "true", "1"].includes(String(c.default).toLowerCase()));
        } else if (ft === "select") {
          const m = (c.options || []).find((o) => [o.key, o.name].map((x) => String(x).toLowerCase()).includes(String(c.default).toLowerCase()));
          _default = m ? (m.key || m.name) : undefined;
        } else {
          _default = (c.default !== undefined && c.default !== null && c.default !== "" && !isNaN(Number(c.default))) ? Number(c.default) : undefined;
        }
        return {
          key: c.key, label: c.label || c.name, help_text: c.help_text || "",
          required: !!c.required, placeholder: c.placeholder || "",
          group: (c.group && c.group.trim()) || "General",
          field_type: ft, options, _default,
        };
      });
    return ordered.map((svc) => {
      const questions = (svc.pricing_v2 && svc.pricing_v2.components) ? buildV2(svc) : [];
      const groups = [];
      const gmap = new Map();
      questions.forEach((f) => {
        const g = (f.group && f.group.trim()) || "General";
        if (!gmap.has(g)) { gmap.set(g, { name: g, factors: [] }); groups.push(gmap.get(g)); }
        gmap.get(g).factors.push(f);
      });
      return { svc, groups, factors: questions };
    });
  }, [evalAnswers, selectedServices, secOrder]);

  // seed component defaults so live pricing reflects them and required-with-default counts as answered
  useEffect(() => {
    if (step !== 3) return;
    const add = {};
    pqServices.forEach((e) => e.factors.forEach((f) => {
      if (f._default !== undefined && f._default !== null && f._default !== "" && answers[f.key] === undefined) add[f.key] = f._default;
    }));
    if (Object.keys(add).length) setAnswers((a) => ({ ...a, ...add }));
  }, [step, pqServices]); // eslint-disable-line react-hooks/exhaustive-deps

  // keep the open service card valid as selection/visibility changes
  useEffect(() => {
    if (step === 3 && pqServices.length && !pqServices.some((e) => e.svc.id === activeSvc)) setActiveSvc(pqServices[0].svc.id);
  }, [step, pqServices, activeSvc]);

  // a question counts as answered once it has a value (booleans always have a state)
  const isAnswered = (f) => {
    if (f.field_type === "boolean") return true;
    const v = answers[f.key];
    return v !== undefined && v !== null && String(v).trim() !== "";
  };
  const FREQ_SHORT = { monthly: "/mo", quarterly: "/qtr", annual: "/yr", one_off: " one-off" };
  const svcPrice = (sid) => (calc?.services || []).find((s) => s.service_id === sid);
  const priceLabel = (p) => !p ? "—" : (p.billing_frequency === "one_off" ? `${gbp(p.proposed_fee)} one-off` : `${gbp(p.proposed_fee)}${FREQ_SHORT[p.billing_frequency] || "/mo"}`);

  // Itemised breakdown of every one-off charge (one-off services + fee alignment + custom items).
  const oneOffSummary = () => {
    const t = calc?.totals;
    if (!t) return null;
    const rows = [
      ...(calc.services || [])
        .filter((s) => (s.billing_frequency === "one_off" || s.presentation === "one_off") && Number(s.proposed_fee) > 0)
        .map((s) => ({ key: `svc-${s.service_id}`, label: s.client_name, amount: s.proposed_fee })),
      ...(t.fee_alignment || [])
        .filter((fa) => !fa.excluded && Number(fa.alignment_fee) > 0)
        .map((fa) => ({ key: `fa-${fa.service_id}`, label: `Fee alignment — ${fa.service_name}`, amount: fa.alignment_fee })),
      ...(t.one_off_items || [])
        .filter((it) => Number(it.price) > 0)
        .map((it) => ({ key: `oo-${it.id}`, label: it.title || "One-off item", amount: it.price })),
    ];
    if (rows.length === 0) return null;
    return (
      <Card className="p-4 rounded-md mt-4 border-cass-navy/15 bg-cass-bone/40" data-testid="one-off-summary">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-alt font-semibold text-cass-navy">One-off summary</h4>
          <span className="text-xs text-cass-slate">One-time charges</span>
        </div>
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.key} className="flex justify-between text-sm" data-testid={`one-off-summary-row-${r.key}`}>
              <span className="text-cass-slate">{r.label}</span>
              <span className="tabular font-medium text-cass-navy">{gbp(r.amount)}</span>
            </div>
          ))}
        </div>
        <Separator className="my-2" />
        <div className="flex justify-between">
          <span className="font-semibold text-cass-navy">Total one-off</span>
          <span className="tabular font-bold text-cass-navy" data-testid="one-off-summary-total">{gbp(t.one_off)}</span>
        </div>
      </Card>
    );
  };
  const svcConfigured = (entry) => entry.factors.every((f) => !f.required || isAnswered(f));
  const allConfigured = pqServices.every(svcConfigured);
  const renderField = (f) => {
    const radio = f.field_type === "select" && f.control_style === "radio";
    return (
      <div key={f.key} data-testid={`q-wrap-${f.key}`}>
        <Label className="text-xs flex items-center gap-1">{f.label}{f.required && <span className="text-red-500" data-testid={`q-required-${f.key}`}>*</span>}</Label>
        {f.field_type === "number" && (
          <>
          <Input data-testid={`q-${f.key}`} type="number" placeholder={f.placeholder || ""} value={answers[f.key] ?? ""} readOnly={f.key === "total_employees" && payrollDrivesEmployees} disabled={f.key === "total_employees" && payrollDrivesEmployees} onChange={(e) => setAnswers((a) => ({ ...a, [f.key]: e.target.value }))} />
          {f.key === "total_employees" && payrollDrivesEmployees && <p className="text-xs text-cass-slate mt-1">Auto-calculated from Payroll (Employees + Variable pay). Included employees are deducted in the fee.</p>}
          </>
        )}
        {f.field_type === "text" && (
          <Input data-testid={`q-${f.key}`} placeholder={f.placeholder || ""} value={answers[f.key] ?? ""} onChange={(e) => setAnswers((a) => ({ ...a, [f.key]: e.target.value }))} />
        )}
        {radio && (
          <RadioGroup value={answers[f.key] ?? ""} onValueChange={(v) => setAnswers((a) => ({ ...a, [f.key]: v }))} className="mt-1 space-y-1" data-testid={`q-${f.key}`}>
            {(f.options || []).map(([val, lbl]) => (
              <label key={val} className="flex items-center gap-2 text-sm cursor-pointer"><RadioGroupItem value={val} data-testid={`q-${f.key}-opt-${val}`} />{lbl}</label>
            ))}
          </RadioGroup>
        )}
        {f.field_type === "select" && !radio && (
          <Select value={answers[f.key] ?? ""} onValueChange={(v) => setAnswers((a) => ({ ...a, [f.key]: v }))}>
            <SelectTrigger data-testid={`q-${f.key}`}><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
            <SelectContent>{(f.options || []).map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {f.field_type === "boolean" && (() => {
          const on = answers[f.key] === true || ["yes", "true", "1"].includes(String(answers[f.key]).toLowerCase());
          return <div className="flex items-center gap-2 h-9"><Switch data-testid={`q-${f.key}`} checked={on} onCheckedChange={(v) => setAnswers((a) => ({ ...a, [f.key]: !!v }))} /><span className="text-sm text-cass-slate">{on ? "Yes" : "No"}</span></div>;
        })()}
        {f.help_text && <p className="text-[11px] text-cass-slate mt-1">{f.help_text}</p>}
      </div>
    );
  };

  const runCalc = async () => {
    const svcInput = Object.entries(selected)
      .filter(([, v]) => v.included)
      .map(([sid, v]) => ({
        service_id: sid, included: true, presentation: v.presentation || "recommended",
        manual_fee: manual[sid]?.fee ?? null, manual_reason: manual[sid]?.reason ?? null,
      }));
    if (!svcInput.length) { setCalc(null); return; }
    const { data } = await api.post("/pricing/calculate", { services: svcInput, answers, year_end: yearEnd || null, cs_date: csDate || null, start_date: effectiveDate || null, fee_alignment_overrides: alignOverrides, one_off_items: oneOffItems });
    setCalc(data);
  };

  const calcSnapshot = useMemo(() => JSON.stringify({
    s: Object.entries(selected).filter(([, v]) => v.included).map(([id, v]) => [id, v.presentation || "recommended", manual[id]?.fee ?? null]),
    a: answers, ye: yearEnd, cs: csDate, sd: effectiveDate, ov: alignOverrides, oo: oneOffItems,
  }), [selected, manual, answers, yearEnd, csDate, effectiveDate, alignOverrides, oneOffItems]);
  useEffect(() => { if (step >= 3) runCalc(); /* eslint-disable-next-line */ }, [step, calcSnapshot]);

  const autoCatchup = async () => {
    if (!yearEnd && !csDate && !startDate) return toast.error("Enter at least one driver date (year-end, CS01 or start date)");
    const svcInput = Object.entries(selected).filter(([, v]) => v.included)
      .map(([sid, v]) => ({ service_id: sid, included: true, presentation: v.presentation || "recommended" }));
    const { data } = await api.post("/pricing/catchup-suggest", { year_end_date: yearEnd, cs_date: csDate, start_date: startDate, services: svcInput, answers });
    if (!data.items.length) return toast.error("No annual services with a matching date to pro-rate");
    setCatchUp(data.items);
    toast.success(`Pro-rated ${data.items.length} annual service(s)`);
  };

  const catchUpGross = Math.round(catchUp.reduce((a, c) => a + (Number(c.net) || 0), 0) * 120) / 100;
  useEffect(() => {
    if (catchUpMethod === "one_off" || catchUpGross <= 0) { setCatchUpPlan(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/pricing/catchup-plan", { gross: catchUpGross, method: catchUpMethod, months: catchUpMonths ? Number(catchUpMonths) : null, start_date: effectiveDate || null, year_end: yearEnd || null });
        setCatchUpPlan(data);
      } catch { setCatchUpPlan(null); }
    }, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [catchUpMethod, catchUpMonths, catchUpGross, effectiveDate, yearEnd]);

  const alignNet = Math.round((calc?.totals?.fee_alignment || []).reduce((a, fa) => fa.excluded ? a : a + (Number(fa.alignment_fee) || 0), 0) * 100) / 100;
  useEffect(() => {
    if (alignMethod === "one_off" || alignNet <= 0) { setAlignPlan(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/pricing/catchup-plan", { gross: alignNet, method: alignMethod, months: alignMonths ? Number(alignMonths) : null, start_date: effectiveDate || null, year_end: yearEnd || null });
        setAlignPlan(data);
      } catch { setAlignPlan(null); }
    }, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [alignMethod, alignMonths, alignNet, effectiveDate, yearEnd]);

  const buildPayload = () => ({
    proposal_type: ptype,
    structure,
    client_id: clientId || null,
    answers,
    services: Object.entries(selected).filter(([, v]) => v.included).map(([sid, v]) => ({
      service_id: sid, included: true, presentation: v.presentation || "recommended",
      manual_fee: manual[sid]?.fee ?? null, manual_reason: manual[sid]?.reason ?? null,
    })),
    renewal_source_id: renewalSourceId || null,
    show_current_fee_to_client: showCurrent,
    renewal_explanation: renewalExplanation,
    catch_up_items: catchUp.filter((c) => c.name).map((c) => ({ name: c.name, period: c.period, net: Number(c.net) || 0 })),
    catch_up_due_date: catchUpDue || null,
    catch_up_payment: { method: catchUpMethod, months: catchUpMonths ? Number(catchUpMonths) : null },
    effective_date: effectiveDate || null,
    expiry_date: expiryDate || null,
    year_end: yearEnd || null,
    cs_date: csDate || null,
    fee_alignment_overrides: alignOverrides,
    fee_alignment_payment: { method: alignMethod, months: alignMonths ? Number(alignMonths) : null },
    one_off_items: oneOffItems,
    builder_step: step,
    builder_pq_index: pqIndex,
    proposal_format: "single",
    annual_uplift: { enabled: annualUplift.enabled, pct: Number(annualUplift.pct) || 0 },
    ...(ptype === "variation" ? { variation_source_id: variationSourceId, variation_source_ref: variationSourceRef, effective_policy: "next_billing_cycle" } : {}),
  });

  const saveProposal = async () => {
    const payload = buildPayload();
    if (structure === "multi_option" && activeOptionId) payload.option_id = activeOptionId;
    if (proposalId) {
      const { data } = await api.put(`/proposals/${proposalId}`, payload);
      setProposalRef(data.reference); setToken(data.token);
      if (data.structure === "multi_option") { setOptions(data.options || []); if (!activeOptionId) setActiveOptionId((data.options || [])[0]?.id || null); }
      return data;
    }
    const { data } = await api.post("/proposals", payload);
    setProposalId(data.id); setProposalRef(data.reference);
    if (data.structure === "multi_option") { setOptions(data.options || []); setActiveOptionId((data.options || [])[0]?.id || null); }
    return data;
  };

  // Multi-option management (Phase 1: up to two options). Switching saves the current option first.
  const switchOption = async (oid) => {
    if (oid === activeOptionId || !proposalId) return;
    try { await saveProposal(); } catch { /* ignore */ }
    const { data } = await api.get(`/proposals/${proposalId}`);
    setOptions(data.options || []);
    const o = (data.options || []).find((x) => x.id === oid);
    if (!o) return;
    setActiveOptionId(oid);
    applyProposal({ ...data, ...o });
  };
  const addOptionBtn = async (mode) => {
    if (!proposalId) { toast.error("Save the draft first (continue past the services step)"); return; }
    try {
      await saveProposal();
      const { data } = await api.post(`/proposals/${proposalId}/options`, mode === "dup" ? { mode: "duplicate", from: activeOptionId } : { mode: "blank" });
      setOptions(data.options);
      setActiveOptionId(data.option_id);
      const full = await api.get(`/proposals/${proposalId}`);
      const nw = (full.data.options || []).find((o) => o.id === data.option_id);
      applyProposal({ ...full.data, ...nw });
      toast.success("Option added");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add option"); }
  };
  const removeOptionBtn = async (oid) => {
    try {
      const { data } = await api.delete(`/proposals/${proposalId}/options/${oid}`);
      setOptions(data.options);
      const first = data.options[0];
      setActiveOptionId(first.id);
      const full = await api.get(`/proposals/${proposalId}`);
      applyProposal({ ...full.data, ...first });
      toast.success("Option removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove option"); }
  };
  const setOptionMeta = async (oid, patch) => {
    try { const { data } = await api.put(`/proposals/${proposalId}/options/${oid}/meta`, patch); setOptions(data.options); }
    catch { toast.error("Could not update option"); }
  };

  // Manual adjustments require a reason before the proposal can advance or be sent.
  const manualReasonMissing = () => (calc?.breakdowns || []).filter((b) => {
    const m = manual[b.service_id];
    return m && Number(m.fee) !== b.recommended_fee && !(m.reason || "").trim();
  }).map((b) => b.client_name);

  const next = async () => {
    if (step === 4) {
      const missing = manualReasonMissing();
      if (missing.length) { toast.error(`Add a reason for the manual fee on: ${missing.join(", ")}`); return; }
      const saved = await saveProposal();
      const pid = proposalId || saved?.id;
      if (pid) { try { await api.put(`/proposals/${pid}/discount`, { ...disc, value: Number(disc.value) || 0 }); } catch { toast.error("Could not save discount"); } }
      toast.success("Draft saved");
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setStep((s) => Math.max(s - 1, 0)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const saveDraftExit = async () => {
    await saveProposal();
    toast.success("Saved as draft");
    navigate("/proposals");
  };

  const [autoStatus, setAutoStatus] = useState("idle"); // idle | saving | saved
  const autoRef = useRef({ timer: null, busy: false, last: "" });
  const autoSnapshot = JSON.stringify(buildPayload());
  useEffect(() => {
    if (step < 2) return;                       // only from the Services step onward
    if (!clientId && !proposalId) return;       // need a client before we can create a draft
    if (autoSnapshot === autoRef.current.last) return;
    if (autoRef.current.timer) clearTimeout(autoRef.current.timer);
    autoRef.current.timer = setTimeout(async () => {
      if (autoRef.current.busy) return;
      autoRef.current.busy = true;
      setAutoStatus("saving");
      try { await saveProposal(); autoRef.current.last = autoSnapshot; setAutoStatus("saved"); }
      catch { setAutoStatus("idle"); }
      finally { autoRef.current.busy = false; }
    }, 1500);
    return () => autoRef.current.timer && clearTimeout(autoRef.current.timer);
  }, [autoSnapshot, step, clientId, proposalId]);

  const doSend = async () => {
    if (!send.recipient_email) return toast.error("Recipient email required");
    const missing = manualReasonMissing();
    if (missing.length) return toast.error(`Add a reason for the manual fee on: ${missing.join(", ")}`);
    setSending(true);
    try {
      const p = await saveProposal();
      const pid = p.id || proposalId;
      const cc = clients.find((c) => c.id === clientId);
      const sc = (cc?.contacts || []).filter((x) => x.signing_authority && x.email);
      const chosen = sc.filter((c) => signerSel.includes(c.email)).map((c) => ({ name: c.name, email: c.email, job_title: c.job_title || "" }));
      const multi = sc.length >= 2 && chosen.length >= 2;
      const { data } = await api.post(`/proposals/${pid}/send`, { ...send, expiry_date: expiryDate || null, custom_subject: emailCopy.subject || null, custom_body: emailCopy.body || null, save_as_default: saveDefault, signers: multi ? chosen : null, signing_policy: signPolicy });
      setEmailSent(!!data.email_sent);
      setSentLink(data.link || (data.token ? `${window.location.origin}/p/${data.token}` : ""));
      toast.success(multi ? `Proposal sent to ${chosen.length} signatories` : (data.email_sent ? "Proposal sent by email" : "Proposal ready (email not delivered)"));
    } catch (e) {
      toast.error("Could not send proposal");
    }
    setSending(false);
  };

  const doCreateLink = async () => {
    const missing = manualReasonMissing();
    if (missing.length) return toast.error(`Add a reason for the manual fee on: ${missing.join(", ")}`);
    setSending(true);
    try {
      const p = await saveProposal();
      const pid = p.id || proposalId;
      const cc = clients.find((c) => c.id === clientId);
      const sc = (cc?.contacts || []).filter((x) => x.signing_authority && x.email);
      const chosen = sc.filter((c) => signerSel.includes(c.email)).map((c) => ({ name: c.name, email: c.email, job_title: c.job_title || "" }));
      const multi = sc.length >= 2 && chosen.length >= 2;
      const { data } = await api.post(`/proposals/${pid}/send`, { ...send, link_only: true, expiry_date: expiryDate || null, signers: multi ? chosen : null, signing_policy: signPolicy });
      setEmailSent(false);
      setLinkOnly(true);
      setSentLink(data.link || (data.token ? `${window.location.origin}/p/${data.token}` : ""));
      setReviewOpen(true);
    } catch (e) {
      toast.error("Could not create link");
    }
    setSending(false);
  };

  const activeOpt = options.find((o) => o.id === activeOptionId) || null;

  return (
    <div>
      <div className="px-8 py-5 border-b border-border bg-white/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <h1 className="font-alt text-2xl font-bold text-cass-navy">
            {ptype === "variation" ? "Variation" : ptype === "renewal" ? "New Renewal" : "New Proposal"} {proposalRef && <span className="text-cass-slate text-base font-normal">· {proposalRef}</span>}
          </h1>
          <div className="flex items-center gap-3">
            {step >= 2 && (
              <span className="text-xs text-cass-slate flex items-center gap-1" data-testid="autosave-status">
                {autoStatus === "saving" ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Saving…</> : autoStatus === "saved" ? <><Check className="w-3.5 h-3.5 text-emerald-600" />Saved</> : null}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={saveDraftExit} data-testid="save-draft-btn">Save draft</Button>
          </div>
        </div>
        <div className="flex items-center gap-1 mt-4">
          {STEPS.map((label, i) => {
            const clickable = i <= maxStep;
            return (
            <div key={label} className="flex items-center gap-1 flex-1">
              <button type="button" onClick={() => goToStep(i)} disabled={!clickable}
                className={`flex items-center gap-2 text-xs font-medium transition-opacity ${i <= step ? STEP_THEME[i].text : "text-cass-slate/50"} ${clickable ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
                data-testid={`step-nav-${i}`}>
                <span className={`w-6 h-6 rounded-full grid place-items-center text-[11px] ${i < step ? `${STEP_THEME[i].dot} text-white` : i === step ? `${STEP_THEME[i].dot} text-white ring-4 ${STEP_THEME[i].ring}` : "bg-slate-200 text-slate-500"}`}>
                  {i < step ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                <span className="hidden lg:inline">{label}</span>
              </button>
              {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < step ? STEP_THEME[i].line : "bg-border"}`} />}
            </div>
            );
          })}
        </div>
      </div>

      {structure === "multi_option" && proposalId && (
        <div className="px-8 py-3 border-b border-border bg-cass-bone/60" data-testid="options-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-cass-navy uppercase tracking-wide mr-1">Options:</span>
            {options.map((o) => (
              <button key={o.id} type="button" onClick={() => switchOption(o.id)}
                className={`px-3 py-1.5 rounded-lg border text-sm ${o.id === activeOptionId ? "border-cass-teal ring-2 ring-cass-teal/20 bg-white" : "border-border bg-white/70 hover:border-cass-teal/50"}`}
                data-testid={`option-tab-${o.order}`}>
                <span className="font-medium text-cass-navy">{o.title}</span>
                {o.recommended && <span className="ml-1.5 text-[10px] text-cass-teal font-semibold">★</span>}
                <span className="ml-2 text-xs text-cass-slate">{gbp(o.totals?.monthly_recurring)}/mo · {gbp(o.totals?.one_off)} one-off</span>
              </button>
            ))}
            {options.length < 3 && (
              <>
                <Button size="sm" variant="outline" onClick={() => addOptionBtn("dup")} data-testid="option-add-dup"><Plus className="w-3.5 h-3.5 mr-1" />Duplicate</Button>
                <Button size="sm" variant="outline" onClick={() => addOptionBtn("blank")} data-testid="option-add-blank"><Plus className="w-3.5 h-3.5 mr-1" />New option</Button>
              </>
            )}
          </div>
          {activeOpt && (
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <Input defaultValue={activeOpt.title || ""} key={`t-${activeOpt.id}`} onBlur={(e) => setOptionMeta(activeOptionId, { title: e.target.value })} className="h-8 w-44 text-sm" data-testid="option-title-input" placeholder="Option title" />
              <Input defaultValue={activeOpt.description || ""} key={`d-${activeOpt.id}`} onBlur={(e) => setOptionMeta(activeOptionId, { description: e.target.value })} className="h-8 flex-1 min-w-[220px] text-sm" data-testid="option-desc-input" placeholder="Short description shown to the client" />
              <label className="flex items-center gap-1.5 text-xs text-cass-navy cursor-pointer" data-testid="option-recommend-toggle">
                <input type="checkbox" checked={!!activeOpt.recommended} onChange={(e) => setOptionMeta(activeOptionId, { recommended: e.target.checked })} />Recommended by CASS
              </label>
              {options.length > 1 && <button type="button" onClick={() => removeOptionBtn(activeOptionId)} className="text-xs text-red-500 hover:underline" data-testid="option-remove">Remove this option</button>}
            </div>
          )}
        </div>
      )}

      <div className="p-8 max-w-4xl">
        <div className={`mb-6 rounded-xl border ${STEP_THEME[step].border} ${STEP_THEME[step].tile} flex items-stretch overflow-hidden`} data-testid="builder-section-banner">
          <div className={`w-1.5 ${STEP_THEME[step].strip}`} />
          <div className="px-5 py-3 flex items-center gap-3">
            <span className={`w-9 h-9 rounded-lg grid place-items-center text-white font-semibold ${STEP_THEME[step].dot}`}>{step + 1}</span>
            <div>
              <div className={`font-alt font-semibold ${STEP_THEME[step].textStrong}`}>{STEPS[step]}</div>
              <div className="text-xs text-cass-slate">{STEP_SUB[step]}</div>
            </div>
          </div>
        </div>
        {/* STEP 0: TYPE */}
        {step === 0 && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              {[["new", "New Proposal", "A new prospect, client, entity or service arrangement.", FileText],
                ["renewal", "Renewal", "Review, update or replace an existing agreement.", RefreshCw]].map(([val, title, desc, Icon]) => (
                <Card key={val} data-testid={`type-${val}`} onClick={() => setPtype(val)}
                  className={`p-6 rounded-md cursor-pointer transition-colors duration-200 ${ptype === val ? "border-cass-teal ring-2 ring-cass-teal/20" : "border-border hover:border-cass-teal/50"}`}>
                  <Icon className="w-8 h-8 text-cass-teal mb-3" />
                  <h3 className="font-alt font-semibold text-cass-navy text-lg">{title}</h3>
                  <p className="text-sm text-cass-slate mt-1">{desc}</p>
                </Card>
              ))}
            </div>
            <div>
              <Label className="text-cass-navy">How should this proposal be structured?</Label>
              <div className="grid md:grid-cols-2 gap-4 mt-2">
                {[["standard", "Standard Proposal", "One way of working. Behaves exactly as today."],
                  ["multi_option", "Proposal with Multiple Options", "Offer up to three alternative arrangements; the client compares and picks one."]].map(([val, title, desc]) => (
                  <Card key={val} data-testid={`structure-${val}`} onClick={() => { if (!proposalId) setStructure(val); }}
                    className={`p-5 rounded-md transition-colors duration-200 ${proposalId ? "opacity-70 cursor-default" : "cursor-pointer"} ${structure === val ? "border-cass-teal ring-2 ring-cass-teal/20" : "border-border hover:border-cass-teal/50"}`}>
                    <h3 className="font-alt font-semibold text-cass-navy">{title}</h3>
                    <p className="text-sm text-cass-slate mt-1">{desc}</p>
                  </Card>
                ))}
              </div>
              {structure === "multi_option" && (
                <p className="text-xs text-cass-teal mt-2" data-testid="structure-hint">You'll build Option 1 now; after the Review step you can add up to two more options (duplicate or new). An options switcher also appears at the top of the builder once the draft is saved.</p>
              )}
              {proposalId && <p className="text-xs text-cass-slate mt-2">Structure is fixed once the draft is created.</p>}
            </div>
          </div>
        )}

        {/* STEP 1: CLIENT */}
        {step === 1 && (
          <Card className="p-6 rounded-md space-y-4">
            <div>
              <Label>Select client / prospect</Label>
              <SearchSelect
                value={clientId}
                onChange={setClientId}
                options={clients.map((c) => ({ value: c.id, label: `${c.trading_name || c.legal_name} (${c.client_type})` }))}
                placeholder="Choose a record"
                searchPlaceholder="Type a client name…"
                testid="select-client"
              />
              <p className="text-xs text-cass-slate mt-1">Need a new record? <a className="text-cass-teal underline" href="/clients">Create one in Clients</a>.</p>
            </div>
            {ptype === "renewal" && clientId && (
              <div>
                <Label>Proposal being renewed</Label>
                <SearchSelect
                  value={renewalSourceId}
                  onChange={prefillRenewal}
                  options={sourceProposals.map((p) => ({ value: p.id, label: `${p.reference} · ${gbp(p.totals?.monthly_recurring)}/mo` }))}
                  placeholder="Select accepted proposal"
                  searchPlaceholder="Type a reference…"
                  testid="select-source-proposal"
                />
                {!sourceProposals.length && <p className="text-xs text-cass-slate mt-1">No accepted proposals for this client.</p>}
              </div>
            )}
          </Card>
        )}

        {/* STEP 2: SERVICES */}
        {step === 2 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-cass-slate" data-testid="services-count">{selectedServices.length} service{selectedServices.length === 1 ? "" : "s"} selected</p>
            </div>
            {Object.entries(services.reduce((a, s) => { (a[s.category] ||= []).push(s); return a; }, {}))
              .sort(([a], [b]) => { const ia = secOrder.indexOf(a), ib = secOrder.indexOf(b); return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib); })
              .map(([cat, items]) => (
              <Card key={cat} className={`rounded-md overflow-hidden border-l-4 ${catTheme(cat).borderL} ${catTheme(cat).card}`} data-testid={`svc-category-${cat}`}>
                <div className={`px-5 py-2.5 ${catTheme(cat).head}`}>
                  <h3 className={`font-alt font-semibold ${catTheme(cat).text}`}>{cat}</h3>
                </div>
                <div className="p-5 space-y-2">
                  {items.map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-3">
                      <label className="flex items-center gap-3 cursor-pointer flex-1">
                        <Checkbox data-testid={`svc-${s.internal_name}`} checked={!!selected[s.id]?.included}
                          onCheckedChange={(v) => setSelected((p) => ({ ...p, [s.id]: { ...p[s.id], included: !!v, presentation: p[s.id]?.presentation || "recommended" } }))} />
                        <div>
                          <div className="text-sm font-medium text-cass-navy">{s.client_name}</div>
                          <div className="text-xs text-cass-slate">{s.short_description}</div>
                        </div>
                      </label>
                      {selected[s.id]?.included && (
                        <Select value={selected[s.id]?.presentation || "recommended"} onValueChange={(v) => setSelected((p) => ({ ...p, [s.id]: { ...p[s.id], presentation: v } }))}>
                          <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["recommended", "optional", "included", "one_off"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* STAGE 2: CONFIGURE PRICING — one card per selected service */}
        {step === 3 && (
          <div className="space-y-4" data-testid="configure-pricing">
            {pqServices.length === 0 && <p className="text-cass-slate" data-testid="pq-empty">No pricing questions for the selected services — continue to review.</p>}
            {pqServices.length > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-cass-slate" data-testid="configure-progress">{pqServices.filter(svcConfigured).length} of {pqServices.length} services configured</p>
                  {!allConfigured && <span className="text-xs text-cass-bronze inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Answer the required questions to continue</span>}
                </div>
                <Accordion type="multiple" defaultValue={pqServices.map((e) => e.svc.id)} className="space-y-3">
                  {pqServices.map((entry) => {
                    const configured = svcConfigured(entry);
                    const theme = catTheme(entry.svc.category);
                    return (
                      <AccordionItem key={entry.svc.id} value={entry.svc.id} className={`border border-border border-l-4 ${theme.borderL} ${theme.card} rounded-md px-4`} data-testid={`svc-card-${entry.svc.internal_name}`}>
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center justify-between w-full pr-3">
                            <div className="flex items-center gap-2 text-left">
                              {configured ? <Check className="w-4 h-4 text-cass-teal shrink-0" data-testid={`svc-configured-${entry.svc.internal_name}`} /> : <span className="w-2 h-2 rounded-full bg-cass-bronze shrink-0" />}
                              <span className={`font-medium ${theme.text}`}>{entry.svc.client_name}</span>
                              {entry.factors.length > 0 && <span className="text-xs text-cass-slate/70">· {entry.factors.length} question{entry.factors.length === 1 ? "" : "s"}</span>}
                            </div>
                            <span className="text-sm text-cass-navy tabular font-semibold" data-testid={`svc-price-${entry.svc.internal_name}`}>{priceLabel(svcPrice(entry.svc.id))}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {svcPrice(entry.svc.id)?.renewal_fee != null && (
                            <p className="text-xs text-cass-teal pb-2" data-testid={`renewal-note-${entry.svc.internal_name}`}>From {svcPrice(entry.svc.id).renewal_date}: {gbp(svcPrice(entry.svc.id).renewal_fee)}/mo (+{svcPrice(entry.svc.id).renewal_pct}% renewal uplift)</p>
                          )}
                          {entry.factors.length === 0 && <p className="text-sm text-cass-slate pb-3">No questions — this service is priced automatically.</p>}
                          {entry.groups.map((g) => (
                            <div key={g.name} className="mb-4">
                              {entry.groups.length > 1 && <h4 className="text-[11px] font-semibold uppercase tracking-wide text-cass-slate/70 mb-2">{g.name}</h4>}
                              <div className="grid md:grid-cols-2 gap-4">{g.factors.map((f) => renderField(f))}</div>
                            </div>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
                {calc?.totals?.fee_alignment && calc.totals.fee_alignment.length > 0 && (
                  <Card className="p-4 rounded-md mt-4 border-cass-teal/40" data-testid="fee-alignment-section">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-alt font-semibold text-cass-navy">Fee Alignment <span className="text-xs font-normal text-cass-slate">(automatic · one-off)</span></h4>
                      <span className="text-sm font-bold text-cass-navy tabular" data-testid="fa-total">{gbp(calc.totals.fee_alignment_total)} one-off</span>
                    </div>
                    <p className="text-xs text-cass-slate mb-3">Accrued fees for months of the current service cycle that elapsed before the proposal start month. Accounts &amp; tax align from the year end; the Confirmation Statement aligns from its CS01 confirmation date.</p>
                    <div className="flex items-end gap-3 mb-3 flex-wrap">
                      <div><Label className="text-xs">Year end (day/month)</Label><DatePicker className="h-8 w-40" value={yearEnd} onChange={setYearEnd} testId="fa-yearend" /></div>
                      <div><Label className="text-xs">CS01 confirmation date</Label><DatePicker className="h-8 w-40" value={csDate} onChange={setCsDate} testId="fa-csdate" /></div>
                      <div><Label className="text-xs">Proposal start date</Label><DatePicker className="h-8 w-40" value={effectiveDate} onChange={setEffectiveDate} testId="fa-startdate" /></div>
                      <div className="text-xs text-cass-slate pb-2">Period: <span className="font-medium">{calc.totals.fee_alignment_period || "—"}</span> · {calc.totals.fee_alignment_months} month(s)</div>
                    </div>
                    <div className="space-y-2">
                      {calc.totals.fee_alignment.map((fa) => {
                        const ov = alignOverrides[fa.service_id] || {};
                        const overriding = ov.excluded || (ov.manual_fee != null && ov.manual_fee !== "");
                        return (
                          <div key={fa.service_id} className="border border-border rounded-md p-3" data-testid={`fa-row-${fa.service_id}`}>
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-cass-navy font-medium">{fa.service_name}</div>
                              <div className="text-sm font-semibold tabular" data-testid={`fa-fee-${fa.service_id}`}>{fa.excluded ? "Excluded" : (fa.alignment_months === 0 ? "No Fee Alignment Required" : gbp(fa.alignment_fee))}</div>
                            </div>
                            <div className="text-xs text-cass-slate mt-1">{gbp(fa.monthly_fee)}/mo × {fa.alignment_months} month(s){fa.alignment_period ? ` (${fa.alignment_period})` : ""}</div>
                            <div className="flex items-center gap-4 mt-2 flex-wrap">
                              <label className="flex items-center gap-2 text-xs text-cass-slate"><Switch checked={!!ov.excluded} onCheckedChange={(v) => setAlignOverrides({ ...alignOverrides, [fa.service_id]: { ...ov, excluded: v } })} data-testid={`fa-exclude-${fa.service_id}`} /> Exclude</label>
                              <Input className="h-7 w-28 text-xs" type="number" placeholder="Manual £" value={ov.manual_fee ?? ""} onChange={(e) => setAlignOverrides({ ...alignOverrides, [fa.service_id]: { ...ov, manual_fee: e.target.value === "" ? null : Number(e.target.value) } })} data-testid={`fa-manual-${fa.service_id}`} />
                              {overriding && (
                                <Input className="h-7 flex-1 min-w-[180px] text-xs" placeholder="Reason (required)" value={ov.reason || ""} onChange={(e) => setAlignOverrides({ ...alignOverrides, [fa.service_id]: { ...ov, reason: e.target.value } })} data-testid={`fa-reason-${fa.service_id}`} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {alignNet > 0 && (
                      <div className="mt-4 pt-4 border-t border-border" data-testid="alignment-payment-control">
                        <h5 className="font-alt font-semibold text-cass-navy text-sm mb-1">How should the Fee Alignment be paid?</h5>
                        <p className="text-xs text-cass-slate mb-2">Total <span className="font-semibold text-cass-navy">{gbp(alignNet)}</span> net (+ VAT). Spread payments are calculated ex-VAT and stay separate from the ongoing monthly fee.</p>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {[["one_off", "One-Off Payment"], ["year_end", "Spread to Year End"], ["custom", "Choose Number of Months"]].map(([v, l]) => (
                            <Button key={v} size="sm" variant={alignMethod === v ? "default" : "outline"} className={alignMethod === v ? "bg-cass-teal text-white" : ""} disabled={v === "year_end" && !yearEnd} onClick={() => setAlignMethod(v)} data-testid={`fa-method-${v}`}>{l}</Button>
                          ))}
                        </div>
                        {!yearEnd && <p className="text-xs text-amber-600 mb-2" data-testid="fa-no-yearend">Add a client year-end above to enable "Spread to Year End".</p>}
                        {alignMethod === "custom" && (
                          <div className="flex items-center gap-2 mb-2"><Label className="text-xs">Number of months</Label><Input type="number" min="1" className="h-8 w-24" value={alignMonths} onChange={(e) => setAlignMonths(e.target.value)} data-testid="fa-months" /></div>
                        )}
                        {alignPlan?.beyond_year_end && <p className="text-xs text-amber-600 mb-2" data-testid="fa-beyond-warning">⚠ This runs past the client's year end. You can still continue if a longer term has been agreed.</p>}
                        {alignPlan?.schedule?.length > 0 && (
                          <div className="border border-border rounded-md overflow-hidden" data-testid="fa-preview">
                            <div className="px-3 py-2 bg-cass-bone text-xs text-cass-slate">Spread over {alignPlan.instalments} payment{alignPlan.instalments === 1 ? "" : "s"} · {alignPlan.start_month} – {alignPlan.end_month}</div>
                            {alignPlan.schedule.map((s) => (<div key={s.n} className="flex justify-between px-3 py-1.5 border-t border-border text-sm"><span className="text-cass-slate">{s.n}. {s.month}</span><span className="tabular">{gbp(s.amount)} + VAT</span></div>))}
                            <div className="flex justify-between px-3 py-2 border-t border-border bg-white text-sm font-semibold"><span>Total</span><span className="tabular text-cass-navy">{gbp(alignPlan.gross)} + VAT</span></div>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                )}
                <Card className="p-4 rounded-md mt-4" data-testid="one-off-items-section">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-alt font-semibold text-cass-navy">Custom one-off items <span className="text-xs font-normal text-cass-slate">(one-off)</span></h4>
                    <Button size="sm" variant="outline" onClick={() => setOneOffItems([...oneOffItems, { id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())), title: "", description: "", price: "" }])} data-testid="add-one-off">+ Add one-off item</Button>
                  </div>
                  {oneOffItems.length === 0 && <p className="text-xs text-cass-slate">Add ad-hoc one-off charges (e.g. a data rebuild) with a custom title, description and manual price.</p>}
                  <div className="space-y-2">
                    {oneOffItems.map((it, i) => (
                      <div key={it.id || i} className="border border-border rounded-md p-3 space-y-2" data-testid={`one-off-row-${i}`}>
                        <div className="flex gap-2">
                          <Input className="flex-1" placeholder="Title (shown to client)" value={it.title || ""} onChange={(e) => setOneOffItems(oneOffItems.map((x, xi) => xi === i ? { ...x, title: e.target.value } : x))} data-testid={`one-off-title-${i}`} />
                          <Input className="w-32" type="number" placeholder="£ price" value={it.price ?? ""} onChange={(e) => setOneOffItems(oneOffItems.map((x, xi) => xi === i ? { ...x, price: e.target.value === "" ? "" : Number(e.target.value) } : x))} data-testid={`one-off-price-${i}`} />
                          <Button size="icon" variant="ghost" onClick={() => setOneOffItems(oneOffItems.filter((_, xi) => xi !== i))} data-testid={`one-off-del-${i}`}><X className="w-4 h-4" /></Button>
                        </div>
                        <Textarea rows={2} placeholder="Description (shown to client)" value={it.description || ""} onChange={(e) => setOneOffItems(oneOffItems.map((x, xi) => xi === i ? { ...x, description: e.target.value } : x))} data-testid={`one-off-desc-${i}`} />
                      </div>
                    ))}
                  </div>
                  {calc?.totals?.one_off_items_total > 0 && <div className="text-right text-sm font-bold text-cass-navy mt-2" data-testid="one-off-total">Total one-off items: {gbp(calc.totals.one_off_items_total)}</div>}
                </Card>
                {oneOffSummary()}
                <div className="sticky bottom-0 -mx-1 mt-3 bg-white/95 backdrop-blur border-t border-border px-4 py-3 flex items-center justify-between rounded-b-md shadow-[0_-2px_8px_rgba(0,0,0,0.04)]" data-testid="grand-total-bar">
                  <span className="text-sm text-cass-slate">Running total</span>
                  <div className="flex gap-6 text-sm items-center">
                    <span className="text-cass-navy font-semibold" data-testid="gt-monthly">£{(calc?.totals?.monthly_recurring ?? 0).toFixed(2)}/mo</span>
                    <span className="text-cass-navy font-semibold" data-testid="gt-oneoff">£{(calc?.totals?.one_off ?? 0).toFixed(2)} one-off</span>
                    {calc?.totals?.renewal_from && calc.totals.monthly_from_renewal !== calc.totals.monthly_recurring && (
                      <span className="text-cass-teal font-semibold" data-testid="gt-renewal">£{(calc.totals.monthly_from_renewal).toFixed(2)}/mo from {calc.totals.renewal_from}</span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* STEP 4: REVIEW */}
        {step === 4 && calc && (
          <div className="space-y-4">
            <Card className="p-5 rounded-md" data-testid="builder-video-card">
              <h3 className="font-alt font-semibold text-cass-navy mb-1">Personal video (optional)</h3>
              <p className="text-xs text-cass-slate mb-3">Add a short, personal video message that appears at the top of this client's online proposal. Paste a Loom/YouTube/Vimeo link or upload a clip. It doesn't affect the PDF.</p>
              <ProposalVideoManager proposalId={proposalId} video={video} onChange={setVideo} />
            </Card>
            {catchUpGross > 0 && (
              <Card className="p-5 rounded-md border-cass-teal/40" data-testid="catchup-payment-card">
                <h3 className="font-alt font-semibold text-cass-navy mb-1">How should the Catch-Up Fee be paid?</h3>
                <p className="text-xs text-cass-slate mb-3">Total Catch-Up Fee <span className="font-semibold text-cass-navy">{gbp(catchUpGross)}</span> (gross). Spread payments are kept separate from the ongoing monthly fee.</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[["one_off", "One-Off Payment"], ["year_end", "Spread to Year End"], ["custom", "Choose Number of Months"]].map(([v, l]) => (
                    <Button key={v} size="sm" variant={catchUpMethod === v ? "default" : "outline"} className={catchUpMethod === v ? "bg-cass-teal text-white" : ""} disabled={v === "year_end" && !yearEnd} onClick={() => setCatchUpMethod(v)} data-testid={`cu-method-${v}`}>{l}</Button>
                  ))}
                </div>
                {!yearEnd && <p className="text-xs text-amber-600 mb-2" data-testid="cu-no-yearend">Add a client year-end (Step 3) to enable "Spread to Year End".</p>}
                {catchUpMethod === "custom" && (
                  <div className="flex items-center gap-2 mb-3"><Label className="text-xs">Number of months</Label><Input type="number" min="1" className="h-8 w-24" value={catchUpMonths} onChange={(e) => setCatchUpMonths(e.target.value)} data-testid="cu-months" /></div>
                )}
                {catchUpPlan?.beyond_year_end && <p className="text-xs text-amber-600 mb-2" data-testid="cu-beyond-warning">⚠ This runs past the client's year end. You can still continue if a longer term has been agreed.</p>}
                {catchUpPlan?.schedule?.length > 0 && (
                  <div className="border border-border rounded-md overflow-hidden" data-testid="cu-preview">
                    <div className="px-3 py-2 bg-cass-bone text-xs text-cass-slate">Spread over {catchUpPlan.instalments} payment{catchUpPlan.instalments === 1 ? "" : "s"} · {catchUpPlan.start_month} – {catchUpPlan.end_month}</div>
                    {catchUpPlan.schedule.map((s) => (<div key={s.n} className="flex justify-between px-3 py-1.5 border-t border-border text-sm"><span className="text-cass-slate">{s.n}. {s.month}</span><span className="tabular">{gbp(s.amount)}</span></div>))}
                    <div className="flex justify-between px-3 py-2 border-t border-border bg-white text-sm font-semibold"><span>Total</span><span className="tabular text-cass-navy">{gbp(catchUpPlan.gross)}</span></div>
                  </div>
                )}
              </Card>
            )}
            {ptype === "renewal" && prevTotals && (
              <Card className="p-5 rounded-md bg-cass-navy text-white">
                <h3 className="font-alt font-semibold mb-3">Internal fee comparison</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><div className="text-slate-400">Current MRR</div><div className="text-xl font-bold tabular">{gbp(prevTotals.monthly_recurring)}</div></div>
                  <div><div className="text-slate-400">Proposed MRR</div><div className="text-xl font-bold tabular">{gbp(calc.totals.monthly_recurring)}</div></div>
                  <div><div className="text-slate-400">Movement</div>
                    <div className="text-xl font-bold tabular text-cass-teal">
                      {gbp(calc.totals.monthly_recurring - prevTotals.monthly_recurring)} ({prevTotals.monthly_recurring ? Math.round((calc.totals.monthly_recurring - prevTotals.monthly_recurring) / prevTotals.monthly_recurring * 100) : 0}%)
                    </div></div>
                </div>
                <Separator className="my-3 bg-white/10" />
                <label className="flex items-center gap-2 text-sm">
                  <Switch data-testid="show-current-toggle" checked={showCurrent} onCheckedChange={setShowCurrent} />
                  Show current fee & movement to the client
                </label>
              </Card>
            )}
            <h3 className="font-alt font-semibold text-cass-navy" data-testid="review-internal-heading">Internal pricing breakdown</h3>
            {calc.breakdowns.map((b) => {
              const svc = calc.services.find((s) => s.service_id === b.service_id);
              const theme = catTheme(selectedServices.find((s) => s.id === b.service_id)?.category);
              const cat = selectedServices.find((s) => s.id === b.service_id)?.category;
              return (
                <Card key={b.service_id} className={`p-5 rounded-md border-l-4 ${theme.borderL} ${theme.card}`} data-testid={`review-svc-${b.service_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <h4 className={`font-medium ${theme.text}`}>{b.client_name}</h4>
                      {cat && <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${theme.head} ${theme.text}`} data-testid={`review-cat-badge-${b.service_id}`}>{cat}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-cass-slate" data-testid={`review-recprop-${b.service_id}`}>Rec {gbp(b.recommended_fee)} → Proposed {gbp(b.proposed_fee)} {b.difference !== 0 && <span className="text-cass-bronze">({b.adjustment_pct}%)</span>}</span>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setActiveSvc(b.service_id); setStep(3); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`edit-svc-${b.service_id}`}>Edit</Button>
                      <Badge variant="outline">{svc?.billing_frequency}</Badge>
                    </div>
                  </div>
                  {svc?.renewal_fee != null && <p className="text-xs text-cass-teal mb-2" data-testid={`review-renewal-${b.service_id}`}>From {svc.renewal_date}: {gbp(svc.renewal_fee)}/mo (+{svc.renewal_pct}% renewal uplift)</p>}
                  {canSeePricingDetail ? (
                    <div className="space-y-0.5 text-xs pl-2 border-l-2 border-border mb-3" data-testid={`calc-inspector-${b.service_id}`}>
                      {b.lines.map((l, i) => (
                        <div key={i} className="flex justify-between">
                          <span className={`text-cass-slate ${l.type === "reduction" ? "text-red-600" : ""}`}>{l.label}</span>
                          <span className="tabular">{gbp(l.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between border-t border-border pt-1 mt-1">
                        <span className="text-cass-slate">Service total</span>
                        <span className="tabular font-semibold text-cass-navy">{gbp(b.proposed_fee)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between text-sm mb-3"><span className="text-cass-slate">Fee</span><span className="tabular font-semibold text-cass-navy">{gbp(b.proposed_fee)}</span></div>
                  )}
                  {canAmendFee && (
                    <div className="flex items-center justify-between gap-4 border-t border-border pt-2">
                      <span className="text-xs text-cass-slate">Line adjustment</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-cass-slate">Adjust £</span>
                        <Input data-testid={`adjust-${b.service_id}`} type="number" className="w-28 h-8"
                          value={manual[b.service_id]?.fee ?? b.proposed_fee}
                          onChange={(e) => setManual((p) => ({ ...p, [b.service_id]: { fee: Number(e.target.value), reason: p[b.service_id]?.reason || "" } }))} />
                      </div>
                    </div>
                  )}
                  {manual[b.service_id] && manual[b.service_id].fee !== b.recommended_fee && (
                    <Input data-testid={`reason-${b.service_id}`} placeholder="Reason for manual adjustment (required)" className="mt-2 h-8 text-sm"
                      value={manual[b.service_id]?.reason || ""}
                      onChange={(e) => setManual((p) => ({ ...p, [b.service_id]: { ...p[b.service_id], reason: e.target.value } }))} />
                  )}
                  {svc?.min_fee_active && (
                    <div className="mt-2 rounded-md border border-cass-teal/30 bg-cass-teal/5 p-2 text-xs" data-testid={`min-fee-panel-${b.service_id}`}>
                      <div className="flex justify-between"><span className="text-cass-slate">Calculated from components</span><span className="tabular">{gbp(svc.calculated_fee)}</span></div>
                      <div className="flex justify-between"><span className="text-cass-slate">Minimum monthly fee</span><span className="tabular">{gbp(svc.min_monthly_fee)}</span></div>
                      <div className="flex justify-between font-semibold text-cass-navy"><span>Final fee</span><span className="tabular">{gbp(b.proposed_fee)}</span></div>
                      {svc.min_fee_applied && <div className="flex items-center gap-1 text-cass-teal mt-1" data-testid={`min-fee-applied-${b.service_id}`}><AlertTriangle className="w-3.5 h-3.5" />Minimum fee applied</div>}
                    </div>
                  )}
                  {svc?.below_minimum && (
                    <div className="flex items-center gap-1 text-xs text-cass-bronze mt-2"><AlertTriangle className="w-3.5 h-3.5" />Below configured minimum of {gbp(svc.minimum_fee)} (warning only)</div>
                  )}
                </Card>
              );
            })}
            {ptype === "variation" && varDelta && (
              <Card className="p-5 rounded-md border-cass-teal/40" data-testid="variation-delta-card">
                <h3 className="font-alt font-semibold text-cass-navy mb-1">Changes vs current agreement {varDelta.source_ref ? `(${varDelta.source_ref})` : ""}</h3>
                <div className="space-y-1 text-sm">
                  {varDelta.added.map((x, i) => <div key={`a${i}`} className="flex justify-between text-emerald-700"><span>+ Added: {x.name}</span><span className="tabular">{gbp(x.fee)}</span></div>)}
                  {varDelta.changed.map((x, i) => <div key={`c${i}`} className="flex justify-between text-cass-bronze"><span>~ Changed: {x.name}</span><span className="tabular">{gbp(x.from)} → {gbp(x.to)}</span></div>)}
                  {varDelta.removed.map((x, i) => <div key={`r${i}`} className="flex justify-between text-red-600"><span>− Removed: {x.name}</span><span className="tabular line-through">{gbp(x.fee)}</span></div>)}
                  {!varDelta.added.length && !varDelta.changed.length && !varDelta.removed.length && <p className="text-cass-slate">No changes yet — add, remove or re-price services above.</p>}
                </div>
                <div className="flex justify-between border-t border-border mt-2 pt-2 text-sm font-semibold text-cass-navy"><span>Monthly recurring</span><span className="tabular">{gbp(varDelta.old_monthly)} → {gbp(varDelta.new_monthly)} ({varDelta.monthly_change >= 0 ? "+" : ""}{gbp(varDelta.monthly_change)})</span></div>
                <p className="text-xs text-cass-slate mt-1" data-testid="variation-effective-note">Changes take effect from the next billing cycle. The original agreement stays in place until then.</p>
              </Card>
            )}
            {canAmendFee && (
              <Card className="p-5 rounded-md" data-testid="annual-uplift-builder-card">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-alt font-semibold text-cass-navy">Apply annual uplift at renewal</h3>
                    <p className="text-xs text-cass-slate">Off by default. Turn on to fix an annual % increase on recurring fees at renewal (billing is otherwise by volume).</p>
                  </div>
                  <Switch checked={annualUplift.enabled} onCheckedChange={(v) => setAnnualUplift((a) => ({ ...a, enabled: !!v }))} data-testid="annual-uplift-toggle" />
                </div>
                {annualUplift.enabled && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-cass-slate">Uplift %</span>
                      <Input type="number" step="0.1" min={0} className="w-24 h-8" value={annualUplift.pct} onChange={(e) => setAnnualUplift((a) => ({ ...a, pct: e.target.value }))} data-testid="annual-uplift-pct" />
                    </div>
                    <p className="text-xs text-cass-teal italic" data-testid="annual-uplift-wording">"Fees are reviewed annually and may change based on volume or by up to {Number(annualUplift.pct) || 0}%." — shown to the client on this proposal.</p>
                  </div>
                )}
              </Card>
            )}
            {canAmendFee && (
              <Card className="p-5 rounded-md" data-testid="builder-discount-card">
                <h3 className="font-alt font-semibold text-cass-navy mb-1">Overriding discount (client-facing)</h3>
                <p className="text-xs text-cass-slate mb-3">Applies an overall discount on top of any line adjustments above. Shows as a discount line and reduces the totals on the client PDF and online proposal.</p>
                <div className="flex flex-wrap items-end gap-2">
                  <div><label className="text-xs text-cass-slate">Label</label><Input className="w-40" value={disc.label} onChange={(e) => setDisc({ ...disc, label: e.target.value })} data-testid="discount-label" /></div>
                  <div><label className="text-xs text-cass-slate">Type</label>
                    <select className="block border border-border rounded-md h-10 px-2 text-sm bg-white" value={disc.type} onChange={(e) => setDisc({ ...disc, type: e.target.value })} data-testid="discount-type"><option value="percent">% of net</option><option value="amount">£ amount</option></select>
                  </div>
                  <div><label className="text-xs text-cass-slate">Value</label><Input className="w-28" type="number" value={disc.value} onChange={(e) => setDisc({ ...disc, value: e.target.value })} data-testid="discount-value" /></div>
                  {Number(disc.value) > 0 && <Button variant="ghost" className="text-xs text-red-600 h-9" onClick={() => setDisc({ label: "", type: "percent", value: 0, reason: "" })} data-testid="discount-clear">Clear</Button>}
                </div>
                <div className="mt-2"><label className="text-xs text-cass-slate">Reason (shown on the proposal)</label><Input value={disc.reason} onChange={(e) => setDisc({ ...disc, reason: e.target.value })} placeholder="e.g. Multi-service loyalty discount" data-testid="discount-reason" /></div>
              </Card>
            )}
            {(() => {
              const monthly = Number(calc.totals.monthly_recurring) || 0;
              let amt = 0;
              if (Number(disc.value) > 0) { amt = disc.type === "percent" ? monthly * Number(disc.value) / 100 : Number(disc.value); amt = Math.min(amt, monthly); }
              amt = Math.round(amt * 100) / 100;
              const afterMonthly = Math.round((monthly - amt) * 100) / 100;
              const afterVat = Math.round(afterMonthly * 0.2 * 100) / 100;
              const annualised = amt > 0 ? Math.round(afterMonthly * 12 * 100) / 100 : calc.totals.annualised;
              return (
                <Card className="p-5 rounded-md bg-cass-bone">
                  <div className="flex justify-between text-sm"><span className="text-cass-slate">Net total (monthly)</span><span className="tabular font-medium">{gbp(monthly)}</span></div>
                  {amt > 0 && <div className="flex justify-between text-sm text-cass-teal" data-testid="review-discount-line"><span>{disc.label || "Discount"} {disc.type === "percent" ? `(${disc.value}%)` : ""}</span><span className="tabular font-medium">−{gbp(amt)}</span></div>}
                  <div className="flex justify-between text-sm"><span className="text-cass-slate">VAT</span><span className="tabular font-medium">{gbp(afterVat)}</span></div>
                  <Separator className="my-2" />
                  <div className="flex justify-between"><span className="font-semibold text-cass-navy">Total monthly fee</span><span className="tabular font-bold text-cass-navy text-lg" data-testid="gt-monthly-gross">{gbp(Math.round((afterMonthly + afterVat) * 100) / 100)}</span></div>
                  <div className="flex justify-between text-xs text-cass-slate mt-2"><span>Annualised: {gbp(annualised)}</span></div>
                </Card>
              );
            })()}
            {oneOffSummary()}
            <ProposalAttachment proposalId={proposalId} attachment={attachment} onChange={setAttachment} />
            <div className="flex gap-3">
              <div className="flex-1"><Label className="text-xs">Start / effective date</Label><DatePicker className="w-full" placeholder="Start / effective date" value={effectiveDate} onChange={setEffectiveDate} testId="effective-date" /></div>
              <div className="flex-1"><Label className="text-xs">Signing deadline</Label><DatePicker className="w-full" placeholder="Signing deadline" value={expiryDate} onChange={setExpiryDate} testId="expiry-date" /><p className="text-[11px] text-cass-slate mt-1">Defaults to 4 weeks. The proposal auto-expires if unsigned by this date.</p></div>
            </div>
            {ptype === "renewal" && (
              <Textarea data-testid="renewal-explanation" placeholder="Client-facing explanation for any changes" value={renewalExplanation} onChange={(e) => setRenewalExplanation(e.target.value)} />
            )}
          </div>
        )}
        {step === 4 && !calc && <p className="text-cass-slate">No services selected. Go back and select services.</p>}

        {/* Multi-option manager on Review — the natural point to add the second option. */}
        {step === 4 && structure === "multi_option" && proposalId && (
          <Card className="p-5 rounded-md border-cass-teal/40 bg-cass-bone/40 mt-4" data-testid="review-options-manager">
            <h3 className="font-alt font-semibold text-cass-navy mb-1">Proposal options</h3>
            <p className="text-xs text-cass-slate mb-3">This proposal offers the client a choice. You're editing <b>{activeOpt?.title || "this option"}</b>. Add a second alternative, then switch between them using the bar at the top.</p>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {options.map((o) => (
                <button key={o.id} type="button" onClick={() => switchOption(o.id)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${o.id === activeOptionId ? "border-cass-teal ring-2 ring-cass-teal/20 bg-white" : "border-border bg-white/70 hover:border-cass-teal/50"}`}
                  data-testid={`review-option-tab-${o.order}`}>
                  <span className="font-medium text-cass-navy">{o.title}</span>
                  {o.recommended && <span className="ml-1.5 text-[10px] text-cass-teal font-semibold">★</span>}
                  <span className="ml-2 text-xs text-cass-slate">{gbp(o.totals?.monthly_recurring)}/mo</span>
                </button>
              ))}
            </div>
            {options.length < 2 ? (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => addOptionBtn("dup")} className="bg-cass-navy text-white" data-testid="review-option-add-dup"><Plus className="w-3.5 h-3.5 mr-1" />Add Option 2 — duplicate this one</Button>
                <Button size="sm" variant="outline" onClick={() => addOptionBtn("blank")} data-testid="review-option-add-blank"><Plus className="w-3.5 h-3.5 mr-1" />Add Option 2 — start fresh</Button>
              </div>
            ) : (
              <p className="text-xs text-cass-teal">Both options are set up. Switch between them above or in the top bar to edit each one; mark one as "Recommended by CASS" in the top bar.</p>
            )}
          </Card>
        )}

        {/* STEP 5: CONTENT & SEND */}
        {step === 5 && (
          <Card className="p-6 rounded-md space-y-4 max-w-xl">
            <h3 className="font-alt font-semibold text-cass-navy">Send proposal</h3>
            <p className="text-sm text-cass-slate">The client receives a secure link — no account required. Email verification is required before signing.</p>
            <div><Label>Recipient email *</Label><Input data-testid="send-email" value={send.recipient_email} onChange={(e) => setSend({ ...send, recipient_email: e.target.value })} /></div>
            <div><Label>Recipient name</Label><Input data-testid="send-name" value={send.recipient_name} onChange={(e) => setSend({ ...send, recipient_name: e.target.value })} /></div>
            <div><Label>Personal message</Label><Textarea data-testid="send-message" value={send.message} onChange={(e) => setSend({ ...send, message: e.target.value })} /></div>
            {(() => {
              const cc = clients.find((c) => c.id === clientId);
              const sc = (cc?.contacts || []).filter((x) => x.signing_authority && x.email);
              if (sc.length < 2) {
                return (
                  <p className="text-xs text-cass-slate rounded-md border border-border bg-cass-bone/30 p-3" data-testid="multi-signer-hint">
                    Sending to a single signer. To let multiple people sign (<span className="font-semibold">Any one</span> / <span className="font-semibold">All must sign</span>), mark 2+ contacts as <span className="font-semibold">Signatory</span> — each with an email — on the{" "}
                    <button type="button" className="underline text-cass-navy font-medium" onClick={() => window.open(`/clients/${clientId}`, "_blank")} data-testid="multi-signer-hint-link">client record</button>.
                  </p>
                );
              }
              return (
                <div className="rounded-md border border-cass-teal/40 bg-cass-teal/5 p-3 space-y-2" data-testid="multi-signer-block">
                  <div className="text-xs font-semibold text-cass-navy">This client has {sc.length} signatories — choose who receives &amp; signs</div>
                  {sc.map((c) => (
                    <label key={c.email} className="flex items-center gap-2 text-sm text-cass-navy">
                      <input type="checkbox" checked={signerSel.includes(c.email)} onChange={() => setSignerSel((xs) => xs.includes(c.email) ? xs.filter((e) => e !== c.email) : [...xs, c.email])} data-testid={`signer-opt-${c.email}`} />
                      {c.name} <span className="text-cass-slate text-xs">· {c.email}</span>
                    </label>
                  ))}
                  <div className="pt-1">
                    <Label className="text-xs">Who must sign to approve?</Label>
                    <div className="flex gap-4 mt-1 text-sm text-cass-navy">
                      <label className="flex items-center gap-1"><input type="radio" name="signpolicy" checked={signPolicy === "any"} onChange={() => setSignPolicy("any")} data-testid="policy-any" /> Any one can sign</label>
                      <label className="flex items-center gap-1"><input type="radio" name="signpolicy" checked={signPolicy === "all"} onChange={() => setSignPolicy("all")} data-testid="policy-all" /> All must sign</label>
                    </div>
                  </div>
                  {signerSel.length < 2 && <p className="text-[11px] text-amber-600">Select at least 2 to send to multiple signatories (otherwise it sends to the recipient above).</p>}
                </div>
              );
            })()}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Signing deadline</Label><DatePicker className="w-full" value={expiryDate} onChange={setExpiryDate} testId="send-expiry" />
                {effectiveDate && expiryDate && (() => { const a = parseDate(effectiveDate), b = parseDate(expiryDate); return a && b && b < a; })() && <p className="text-xs text-red-500 mt-1" data-testid="expiry-warning">Signing deadline is before the effective date.</p>}
              </div>
              <div><Label>Access code (optional)</Label><Input data-testid="send-access-code" value={send.access_code} onChange={(e) => setSend({ ...send, access_code: e.target.value })} /></div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={saveDraftExit}>Save draft</Button>
              <div className="space-y-2">
                <div><Label>Email template *</Label>
                  <Select value={selectedTemplateId} onValueChange={(v) => { setSelectedTemplateId(v); const t = emailTemplates.find((x) => x.id === v); if (t) setEmailCopy({ subject: t.subject || "", body: t.body || "" }); }}>
                    <SelectTrigger data-testid="send-template"><SelectValue placeholder="Choose an email template" /></SelectTrigger>
                    <SelectContent>{emailTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {emailTemplates.length === 0 && <p className="text-[11px] text-cass-slate mt-1">No templates yet — add them in Admin → Email Templates.</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={async () => { if (!send.recipient_email) return toast.error("Recipient email required"); if (!selectedTemplateId) return toast.error("Choose an email template"); setLinkOnly(false); setSentLink(""); setReviewOpen(true); }} className="bg-cass-teal text-white" data-testid="send-proposal-btn"><Send className="w-4 h-4 mr-2" />Review &amp; send</Button>
                  <Button variant="outline" onClick={doCreateLink} disabled={sending} data-testid="create-link-btn"><Link2 className="w-4 h-4 mr-2" />Create link only</Button>
                </div>
                <p className="text-[11px] text-cass-slate">"Create link only" issues the proposal and gives you the secure link without emailing — handy when several companies' proposals go to the same person in one email.</p>
              </div>
            </div>
            {reviewOpen && (
              <SendReviewModal open={reviewOpen} send={send} sending={sending} sentLink={sentLink} emailSent={emailSent} linkOnly={linkOnly}
                subject={emailCopy.subject} body={emailCopy.body}
                onSubjectChange={(v) => setEmailCopy((c) => ({ ...c, subject: v }))}
                onBodyChange={(v) => setEmailCopy((c) => ({ ...c, body: v }))}
                saveDefault={saveDefault} onSaveDefaultChange={setSaveDefault}
                onResetDefault={async () => { if (!proposalId) return; try { const { data } = await api.get(`/proposals/${proposalId}/email-defaults`); setEmailCopy({ subject: data.subject || "", body: data.body || "" }); toast.success("Reset to saved default"); } catch { toast.error("Could not reset"); } }}
                onSend={doSend}
                onClose={() => { const go = !!sentLink; setReviewOpen(false); if (go) navigate(`/proposals/${proposalId}`); }} />
            )}
          </Card>
        )}
      </div>

      <div className="px-8 py-4 border-t border-border bg-white flex justify-between max-w-4xl">
        <Button variant="ghost" onClick={back} disabled={step === 0} data-testid="wizard-back"><ChevronLeft className="w-4 h-4 mr-1" />Back</Button>
        {step < STEPS.length - 1 && (
          <Button onClick={next} className="bg-cass-navy text-white" data-testid="wizard-next"
            disabled={(step === 1 && !clientId) || (step === 2 && !Object.values(selected).some((v) => v.included)) || (step === 3 && !allConfigured)}>
            Continue<ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
